import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBase, InvoiceNumberType, InvoiceNumbersFormat, Required, Validators, number, string } from '@sb/library/base';
import { SBDate } from '@sb/library/localization';
import { FormFieldSubformBaseComponent } from '../../form-field-subform-base.component';
import { PropertyLengths } from '@sb/library/data-models';

class InvoiceNumberFormatForm extends FormBase {
    Format = Required(string);
    NextNumber = Required(number);
}

@Component({
    selector: 'lib-input-invoice-number-format',
    templateUrl: './form-field-invoice-number-format.component.html',
    styleUrls: ['./form-field-invoice-number-format.component.scss'],
})
export class FormFieldInvoiceNumberFormatComponent extends FormFieldSubformBaseComponent<InvoiceNumberType | null, InvoiceNumberFormatForm> implements OnInit, AfterViewInit {

    readonly form = InvoiceNumberFormatForm.Create(false);

    private _previewText: string = "";
    private _defaultFormat: string = "%YY%-%NNNN%";
    private _maxNextNumber: number = 10000000;

    writeFormValue(value: InvoiceNumberType): void {
        value.InvoiceFormat = value?.InvoiceFormat ? value?.InvoiceFormat.trim() : this._defaultFormat;
        value = this.InitializeOnWrite(value, InvoiceNumberType);
        this.PatchValue(value);
    }

    ngAfterViewInit(): void {
        this.form.controls.NextNumber.AddValidators(Validators.max(this._maxNextNumber));
        this.form.controls.Format.AddValidators(Validators.maxLength(PropertyLengths.School.InvoiceNumberFormat));
    }

    AddPlaceholder(format: InvoiceNumbersFormat) {
        const formValue = this.form.controls.Format.value as string;
        if (formValue && !formValue.endsWith('-')) {
            format = '-' + format;
        }
        const inserted: string = formValue.concat(format as string);
        this.form.controls.Format.setValue(inserted);
        this.form.controls.Format.isDirty = true;
        this.SetPreviewText(this.form.controls.Format.value);
        this.SetFormControl();
    }

    SetPreviewText(value: string): void {
        let previewText = value;

        const numberMatch = previewText.match(/%([n,N]*)%/g);
        if (numberMatch) {
            numberMatch.forEach(match => {
                const placeholderMatch = match.replace(/%/g, '');
                if (this.form.value.NextNumber != null) {
                    previewText = previewText.replace(match, this.form.value.NextNumber.toString().padStart(placeholderMatch.length, '0'));
                }
            });
        }

        const dateMatch = previewText.match(/%([d,D]{2})%/g);
        if (dateMatch) {
            dateMatch.forEach(match => {
                previewText = previewText.replace(match, SBDate.Today().dayOfMonth.toString().padStart(2, '0'));
            });
        }

        const monthMatch = previewText.match(/%([m,M]{2})%/g);
        if (monthMatch) {
            monthMatch.forEach(match => {
                previewText = previewText.replace(match, SBDate.Today().month.toString().padStart(2, '0'));
            });
        }

        const yearMatch = previewText.match(/%([y,Y]{2})%/g);
        if (yearMatch) {
            yearMatch.forEach(match => {
                previewText = previewText.replace(match, this.GetYYString(SBDate.Today().year));
            });
        }
        const fullYearMatch = previewText.match(/%([y,Y]{4})%/g);
        if (fullYearMatch) {
            fullYearMatch.forEach(match => {
                previewText = previewText.replace(match, SBDate.Today().year.toString());
            });
        }

        this._previewText = previewText;
    }

    InputChanged(): void {
        this.SetPreviewText(this.form.controls.Format.value);
        this.SetFormControl();
    }

    private PatchValue(value: InvoiceNumberType): void {
        this.form.controls.Format.setValue(value.InvoiceFormat!);
        this.form.controls.NextNumber.setValue(value?.InvoiceNextNumber ?? 1);
        this.SetPreviewText(value.InvoiceFormat!);
        this.SetFormControl();
    }

    private SetFormControl(): void {
        this.formControl?.patchValue({
            InvoiceFormat: this.form.controls.Format.value ?? null,
            InvoiceNextNumber: this.form.controls.NextNumber.value!
        }, { emitModelToViewChange: false });
    }

    private GetYYString(YYYY: number): string {
        let YY: number = ((YYYY%1000)%100)
        return YY.toString();
    }

    get InvoiceNumbersFormat(): typeof InvoiceNumbersFormat {
        return InvoiceNumbersFormat;
    }

    get previewText(): string {
        return this._previewText;
    }
}
